// signup component
import { useContext, useEffect, useState } from 'react';
import { useForm, useFieldArray, SubmitHandler } from "react-hook-form";
import { useNavigate } from 'react-router-dom';

import StateContext from '../stateContext';

import axios from 'axios';
import { Link } from 'react-router-dom';

function SignUp() {

  const navigate = useNavigate();
  const { createNotification, validUserCreds } = useContext(StateContext);
  const [ phase, setPhase ] = useState(0);

  // check if user is already logged in
  if (validUserCreds() == true) {
    window.location.href = "/home"
  }

  const {
      getValues,
      register,
      trigger,
      control,
      watch,
      handleSubmit,
      formState: { errors }
    } = useForm({
      defaultValues: {
        name: "",
        phone: "",
        password: "",
        password_confirmation: "",
        email: "",
        location: "",
        website: "",
        experiences: [],
        desired_roles: [],
        skills: [],
        education: []
      }
    });

  // watcher for experience fields and sub fields
  const currentExperiences = watch("experiences");
  const password = watch("password");

  const {
    fields: experienceFields,
    append: appendExperience,
    remove: removeExperience,
    update: updateExperience
  } = useFieldArray({
    control,
    name: "experiences"
  });

  const {
    fields: desiredRoleFields, 
    append: appendDesiredRole,
    remove: removeDesiredRole,
    update: updateDesiredRole
  } = useFieldArray({
    control,
    name: "desired_roles"
  });

  const {
    fields: skillFields, 
    append: appendSkill,
    remove: removeSkill,
    update: updateSkill
  } = useFieldArray({
    control,
    name: "skills"
  });

  const {
    fields: educationFields, 
    append: appendEducation,
    remove: removeEducation,
    update: updateEducation
  } = useFieldArray({
    control,
    name: "education"
  });

  // submit data for sign up
  async function signUp(signUpData) {

    await next();

    // encode password
    signUpData.password = btoa(unescape(encodeURIComponent(signUpData.password.trim())))
    signUpData.password_confirmation = btoa(unescape(encodeURIComponent(signUpData.password_confirmation.trim())))

    // set csrf tokens
    axios.defaults.xsrfCookieName = 'csrftoken';
    axios.defaults.xsrfHeaderName = "X-CSRFTOKEN";
    axios.defaults.withCredentials = true;

    axios.post(
      '/api/account/signUp',
      signUpData
    ).then((response) => {
      if (response.status == 200 && response.data.success == true) {
        if (response.data.results > 0) {
          // reroute user to browse/home page with notification of email verification requirement
          navigate('/login/userCreated');
        } else {
          // no results
          createNotification({
          "status": "error",
          "msg": "Error signing up!",
        })
        }
      } else {
        // prompt error 
        createNotification({
          "status": "error",
          "msg": "Error signing up!",
        })
      }
    })
    
  }

  // skip to next phase
  async function next() {

    // check each form items based on phase
    var fields = [];

    // general info 
    if (phase === 0) {
      fields = ["name", "phone", "email", "password", "password_confirmation", "location", "website"];
      
    // work experience
    } else if(phase === 1) {
      experienceFields.forEach((experience, experienceIndex) => {

        fields.push(`experiences.${experienceIndex}.title`);
        fields.push(`experiences.${experienceIndex}.company`);
        fields.push(`experiences.${experienceIndex}.start_date`);
        
        if (experience.current_position == false) {
          fields.push(`experiences.${experienceIndex}.end_date`);
        }

        experience.responsibilities.forEach((responsibility, responsibilityIndex) => {
          fields.push(`experiences.${experienceIndex}.responsibilities.${responsibilityIndex}.value`);
        })
      })
    
    // desired roles
    } else if(phase === 2) {
      desiredRoleFields.forEach((desiredRole, desiredRoleIndex) => {
        fields.push(`desired_roles.${desiredRoleIndex}.value`);
      })

    // skills
    } else if (phase === 3) {
      skillFields.forEach((skills, skillIndex) => {
        fields.push(`skills.${skillIndex}.value`);
      })
    
    // education
    } else if (phase === 4) {
      educationFields.forEach((educationField, educationFieldIndex ) => {
        fields.push(`education.${educationFieldIndex}.institution`);
        fields.push(`education.${educationFieldIndex}.degree`);
        fields.push(`education.${educationFieldIndex}.start_date`);
      })
    }

    const isValid = await trigger(fields);

    if (isValid && phase < 4) { 
      setPhase(phase + 1);
    } 
  }

  // go to previous phase
  function previous() {

    if (phase <= 1) {
      setPhase(0)
    } else {
      setPhase(phase - 1)
    }
  }


  // responsibility functions
  function addResponsibility(experienceIndex) {
    const curExperience = getValues(`experiences.${experienceIndex}`);
  
    const updatedResponsibilities = [
      ...(curExperience.responsibilities || []), 
      { value: "" }
    ];

    updateExperience(experienceIndex, {
      ...curExperience,
      responsibilities: updatedResponsibilities
    });
  }

  function removeResponsibility(experienceIndex, responsibilityIndex) {
    const curExperience = getValues(`experiences.${experienceIndex}`);
    const updatedResponsibilities = curExperience.responsibilities.filter((_, index) => index !== responsibilityIndex) 

    updateExperience(experienceIndex, {
      ...curExperience,
      responsibilities: updatedResponsibilities
    });
  }

  return (
    <>
      <form 
        onSubmit={handleSubmit(signUp)}
        className="floatingContainer"
        style={{
          width: "80%", maxWidth: "400px", gap: "10px", 
          margin: "auto", display: 'flex', flexDirection: 'column', 
          padding: "30px", maxHeight: "800px", overflowY: "scroll",
          borderRadius: "10px", backgroundColor: "#ffffff"
        }}
      >
        { phase === 0 ? 
        <>
          <h2 className="direction" >Let's get started with info about you</h2>
          <div className='input'>
            <label className="inputLabel" htmlFor="name" >Full Name</label>
            <input {...register(`name`, { required: true, minLength: { value: 2, message: "Full name required!" } })}  placeholder='Enter Full Name' autoComplete="given-name" style={ errors.name ? {border: "2px solid red"} : undefined}/>
          </div>
          <div className='input'>
            <label className="inputLabel" htmlFor="phone" >Phone</label>
            <input {...register(`phone`, { required: true })} placeholder='Enter Phone Number' style={ errors.phone ? {border: "2px solid red"} : undefined}/>
          </div>
          <div className='input'>
            <label className="inputLabel" htmlFor="email" >Email</label>
            <input {...register(`email`, { required: true, pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: "Valid email address requiered!" } })} placeholder='Enter Email Address' style={ errors.email ? {border: "2px solid red"} : undefined}/>
          </div>
          <div className='input'>
            <label className="inputLabel" htmlFor="password" >Password</label>
            <input type="password" {...register(`password`, { required: true, minLength: { value: 4, message: "Password required!" } })} placeholder='Enter Password' style={ errors.password ? {border: "2px solid red"} : undefined}/>
          </div>
          <div className='input'>
            <label className="inputLabel" htmlFor="password_confirmation" >Confirm Password</label>
            <input type="password" {...register(`password_confirmation`, { required: "Password confirmation required!", validate: (value) => value === password || "passwords do not match"})} placeholder='Confirm Password' style={ errors.password_confirmation ? {border: "2px solid red"} : undefined}/>
          </div>
          <div className='input'>
            <label className="inputLabel" htmlFor="location" >Location</label>
            <input {...register(`location`)} placeholder='Enter Location' style={ errors.location ? {border: "2px solid red"} : undefined}/>
          </div>
          <div className='input'>
          <label className="inputLabel" htmlFor="website" >Website</label>
          <input {...register(`website`)} placeholder='Enter Website / URL' style={ errors.website ? {border: "2px solid red"} : undefined}/>
          </div>
          <button type="button" className='submit' onClick={next}>Next</button>

        </>
        : undefined}

        { phase == 1 ?
          <>
            <h2>Lets add some work experience</h2>
            <h6 style={{color: "#757577"}}>(This helps us recommend jobs)</h6>
            { experienceFields.map((experience, experienceIndex) => (
                <div 
                  key={experience.id}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px"
                  }}
                >
                  <div className='input'>
                    <label className="inputLabel" htmlFor={`experiences.${experienceIndex}.title`} >Job Title</label>
                    <input {...register(`experiences.${experienceIndex}.title`, { required: true, minLength: { value: 3, message: "Experience title required!" } })} placeholder='Enter job title...' 
                      style={ errors?.experiences != undefined && errors?.experiences[experienceIndex]?.title ? {border: "2px solid red"} : undefined} 
                    />
                  </div>
                  <div className='input'>
                    <label className="inputLabel" htmlFor={`experiences.${experienceIndex}.company`}  >Company Name</label>
                    <input {...register(`experiences.${experienceIndex}.company`, { required: true, minLength: { value: 4, message: "Company name required!" } })} className='stringInput' placeholder='Company Name' 
                      style={ errors?.experiences && errors?.experiences[experienceIndex]?.company ? {border: "2px solid red"} : undefined}
                    />
                  </div>
                  <div className='input'>
                    <label className="inputLabel" htmlFor={`experiences.${experienceIndex}.location`} >Location</label>
                    <input {...register(`experiences.${experienceIndex}.location`, { required: true, minLength: { value: 4, message: "Location required!" } })} className='stringInput' placeholder='Location' 
                      style={ errors?.experiences && errors?.experiences[experienceIndex]?.location ? {border: "2px solid red"} : undefined}
                    />
                  </div>
                  <div className='input'>
                    <label className="inputLabel" htmlFor="location" >Start Date</label>
                    <input type="date" {...register(`experiences.${experienceIndex}.start_date`, { required: true, message: "Start date needed!" } )} className='stringInput' 
                    style={ errors?.experiences && errors?.experiences[experienceIndex]?.start_date ? {border: "2px solid red"} : undefined}
                    />
                  </div>
                  <div className='input'>
                    <label className="inputLabel" htmlFor="location" >
                      Current Position?
                      <input type="checkbox" {...register(`experiences.${experienceIndex}.current_position`)} />
                    </label>
                  </div>
                  { getValues(`experiences.${experienceIndex}.current_position`) == true ? 
                    undefined 
                    : 
                    <div className='input'>
                      <label className="inputLabel" htmlFor="location" >End Date</label>
                      <input type="date" {...register(`experiences.${experienceIndex}.end_date`, { required: true })} className='stringInput' 
                      style={ errors?.experiences && errors?.experiences[experienceIndex]?.end_date ? {border: "2px solid red"} : undefined}/>
                    </div>
                  }

                  <h4 style={{textAlign: 'left', display: "flex", gap: "10px", alignItems: 'center'}}>
                    <span>Responsibilities</span>
                    { currentExperiences[experienceIndex].responsibilities.length < 4 ? 
                      <button className="submit" onClick={() => {addResponsibility(experienceIndex)}}>+</button> 
                      :
                      undefined 
                    }
                  </h4>
                  { currentExperiences[experienceIndex].responsibilities.map((responsibility, responsibilityIndex) => (
                    <div
                      style={{
                        display: "flex", flexDirection: "row", justifyContent: "space-around"
                      }}
                    >
                      <textarea 
                        key={`exp-${experience.id}-res-${responsibilityIndex}`}
                        rows="3"  
                        {...register(`experiences.${experienceIndex}.responsibilities.${responsibilityIndex}.value`, { required: true })} 
                        placeholder="Enter work responsibilities"
                        style={ errors?.experiences && errors?.experiences[experienceIndex]?.responsibilities[responsibilityIndex]?.value ? 
                          {border: "2px solid red", width: "85%", resize: "none"} 
                          :
                          {width: "85%", resize: "none"}
                        }
                      />
                      <button className="cancel" onClick={() => {removeResponsibility(experienceIndex, responsibilityIndex)}}>x</button>
                    </div>
                  ))}
                  <button className="delete" onClick={() => { removeExperience(experienceIndex)}} >Remove Experience</button>
                </div>
              )
            )}
            <button type="button" className="submit" onClick={() => {appendExperience({
              "title": "",
              "company": "",
              "end_date": "",
              "current_position": false,
              "start_date": "",
              "responsibilities": [{value: ""}],
              "location": ""
            })}}>+ Add New Experience</button>

            <div style={{
              display: 'flex', flexDirection: "row", justifyContent: "space-between"
            }}>
              <button type="button" className="warning" style={{width: "48%"}} onClick={() => {previous()}} >Back</button>
              <button type="button" className="next" style={{width: "48%"}}  onClick={() => {next()}} >{experienceFields.length <= 0 ? "Skip" : "Next"}</button>
            </div>
          </>
        : undefined }

        { phase == 2 ?
          <>
            <h2>What are your desired roles?</h2>
            <div className="scatteredArrayInputContainer">
              
              {
                desiredRoleFields.map((dRole, index) => {
                  return (
                    <div key={`desired_roles-${index}`} className='scatteredArrayInput' style={ errors?.desired_roles && errors?.desired_roles[index]?.value ? {border: "2px solid red"} : undefined}>
                      <input {...register(`desired_roles.${index}.value`, { required: true, minLength: { value: 2, message: "Desired Role Required!" }})} 
                      placeholder="type here" 
                      rows="2" cols="50"/>
                      <button type="button" onClick={() => { removeDesiredRole(index)}} >x</button>
                    </div>
                    );
                })
              }
              { desiredRoleFields.length < 5 ? 
                <div 
                  className="buttonInput"
                  style={{
                    width: "100%"
                  }}
                >
                  <input placeholder='Enter Desired Role' />
                  <button key={"-"} className="submit" type="button" onClick={(e) => {appendDesiredRole({value: e.target.previousElementSibling.value}); e.target.previousElementSibling.value = "";}}>{`Add`}</button>
                </div>
              : undefined}
            </div>
            <div style={{
              display: 'flex', flexDirection: "row", justifyContent: "space-between"
            }}>
              <button type="button" className="warning" style={{width: "48%"}} onClick={() => {previous()}} >Back</button>
              <button type="button" className="next" style={{width: "48%"}}  onClick={() => {next()}} >{desiredRoleFields.length <= 0 ? "Skip" : "Next"}</button>
            </div>
          </>
        : undefined}
        
        { phase === 3 ? 
          <>
            <h2 className="title1">What are your skills?</h2>
            <div className="scatteredArrayInputContainer">
              {
                skillFields.map((skill, index) => {
                  return (
                    <div key={`skill-${index}`} className='scatteredArrayInput' style={ errors?.skills && errors?.skills[index]?.value ? {border: "2px solid red"} : undefined}>
                      <input {...register(`skills.${index}.value`, { required: true, minLength: { value: 2, message: "Desired Role Required!" }})} placeholder="type here" />
                      <button type="button" onClick={() => { removeSkill(index)}} >x</button>
                    </div>
                    );
                })
              }
              { skillFields.length < 20 ? 
                <div 
                  className="buttonInput"
                  style={{
                    width: "100%"
                  }}
                >
                  <input placeholder='Enter Skill' />
                  <button key={"-"} className="submit" type="button" onClick={(e) => {appendSkill({value: e.target.previousElementSibling.value}); e.target.previousElementSibling.value = "";}}>{`Add`}</button>
                </div>
              : undefined}
            </div>
            <div style={{
              display: 'flex', flexDirection: "row", justifyContent: "space-between"
            }}>
              <button type="button" className="warning" style={{width: "48%"}} onClick={() => {previous()}} >Back</button>
              <button type="button" className="next" style={{width: "48%"}}  onClick={() => {next()}} >{skillFields.length <= 0 ? "Skip" : "Next"}</button>
            </div>
          </>
        : undefined }

        { phase === 4 ? 
          <>
            <h2 className="title1">Any educational experience?</h2>
            { 
              educationFields.map((education, index) => (
                <div key={`education-${index}`} style={{display: "flex", flexDirection: "column", gap: "10px"}} >
                  <div className="input">
                    <label className="inputLabel" htmlFor="location" >Institution</label>
                    <input 
                    {...register(`education.${index}.institution`, { required: true , minLength: { value: 3, message: "Institution name required"}})} placeholder='Institution Name' 
                    style={ errors?.education && errors?.education[index]?.institution ? {border: "2px solid red"} : undefined}
                    />
                  </div>
                  <div className="input">
                    <label className="inputLabel" htmlFor="location" >Degree</label>
                    <input {...register(`education.${index}.degree`, { required: true, minLength: { value: 2, message: "Degree required!" } })} 
                    placeholder='Degree Type'
                    style={ errors?.education && errors?.education[index]?.degree ? {border: "2px solid red"} : undefined}
                  />
                  </div>
                  <div className="input">
                    <label className="inputLabel" htmlFor="location" >Start Date</label>
                    <input type="date" {...register(`education.${index}.start_date`, { required: true, minLength: { value: 2, message: "Start date required!" } })} className='stringInput' 
                      style={ errors?.education && errors?.education[index]?.start_date ? {maxWidth: "max-content", border: "2px solid red"} : {maxWidth: "max-content"}}
                    />
                  </div>
                  <div className="input">
                    <label className="inputLabel" htmlFor="location" >End Date</label>
                    <input type="date" {...register(`education.${index}.end_date`)} className='stringInput' style={{maxWidth: "max-content"}}/>
                  </div>
                  <button className="cancel" onClick={() => { removeEducation(index)}} >Remove Education</button>
                </div>
              ))
            }
            <button type="button" className="submit" onClick={() => {appendEducation({
              "institution": "",
              "degree": "",
              "start_date": "",
              "end_date": ""
            })}}>+ New Education</button>
            <div style={{
              display: 'flex', flexDirection: "row", justifyContent: "space-between"
            }}>
              <button className="warning" style={{ width: "48%"}} type="button" onClick={() => {previous()}} >Back</button>
              <button type='submit' className="next" style={{width: "48%"}}>Browse jobs!</button>
            </div>
          </>
        : undefined }
      </form>
    </>
  )
}

export default SignUp;